import PropTypes from 'prop-types'
import { useCallback, useLayoutEffect } from 'react'

import { ModalOverlay, ModalContainer, ModalHeader, ModalBody } from './Modal.styles'

const ESC_KEY = 27

export function Modal({ isOpen, alwaysFull, onClose, children, maxWidth, height }) {
  const handleKeyDown = useCallback(
    event => {
      const { keyCode } = event

      if (keyCode !== ESC_KEY) {
        return false
      }

      event.stopPropagation()
      return onClose(event)
    },
    [onClose]
  )

  const bindKeys = useCallback(() => {
    window.addEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const unbindKeys = useCallback(() => {
    window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useLayoutEffect(() => {
    bindKeys()
    return unbindKeys
  }, [bindKeys, unbindKeys])

  useLayoutEffect(() => {
    // prevents scroll of the bg when modal is open
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      window.scrollTo(0, parseInt(scrollY || '0') * -1)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <ModalOverlay>
      <ModalContainer role="dialog" alwaysFull={alwaysFull} maxWidth={maxWidth} height={height}>
        {children}
      </ModalContainer>
    </ModalOverlay>
  )
}

Modal.Header = ModalHeader
Modal.Body = ModalBody

Modal.propTypes = {
  isOpen: PropTypes.bool,
  alwaysFull: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.number,
  height: PropTypes.number,
}

Modal.defaultProps = {
  isOpen: false,
  alwaysFull: false,
  onClose: Function.prototype,
  maxWidth: 730,
  height: null,
}

export default Modal
